import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Button, Divider, FormField, FormGroup, FormRadio, Header, Icon, Input, Modal, ModalActions, ModalContent, Select } from 'semantic-ui-react';
import { USER_ROLE_ADVISOR } from '../../../../utils/constants';
import { createFollowUpAppointment, getProspectsAppointmentById } from '../../../../services/prospects.service';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../hooks/AuthProvider';
import DropdownOption from '../../../../types/models/DropdownOption';
import { getSettlementsByZipCode } from '../../../../services/sepomex.service';
import DatePicker from 'react-datepicker';
import { setHours, setMinutes } from 'date-fns';
import { getClientAddressesByClientId } from '../../../../services/clients.service';
import ProspectAppointment from '../../../../types/entities/ProspectAppointment';

interface ProspectAddressData {
    street?: string;
    interiorNumber?: string;
    exteriorNumber?: string;
    postalCode?: string;
    city?: string;
    municipality?: string;
    state?: string;
    neighborhood?: string;
}

interface AppointmentData {
    addressType: string;
    dateTime: Date;
    selectedAddressId?: string;
    addressData?: ProspectAddressData | null;
}

interface ProspectRequest {
    appointmentData: AppointmentData;
}

type RadioClientOptions = 'NEW_PROSPECT' | 'EXISTING_CLIENT';
type RadioAddressOptions = 'NEW_ADDRESS' | 'EXISTING_ADDRESS' | 'SHOWROOM';

interface FollowUpAppointmentModalProps {
    prospectAppointmentId: string,
    onProspectUpdated: () => void,
    isOpen: boolean,
    onClose: () => void
}

const FollowUpAppointmentModal = ({
    prospectAppointmentId,
    onProspectUpdated,
    isOpen,
    onClose
}: FollowUpAppointmentModalProps) => {

    const auth = useAuth();
    const userLogged = auth.userData ?? null;
    const isUserAdvisor = userLogged?.role === USER_ROLE_ADVISOR;

    const [prospectAppointment, setProspectAppointment] = useState<ProspectAppointment | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [neighborhoods, setNeighborhoods] = useState<DropdownOption[]>([]);
    const [clientAddresses, setClientAddresses] = useState<DropdownOption[]>([]);

    const [clientType, setClientType] = useState<RadioClientOptions | undefined>(undefined);
    const [addressRadioSelected, setAddressRadioSelected] = useState<RadioAddressOptions | undefined>(undefined);

    const [clientIdSelected, setClientIdSelected] = useState<string>("");
    const [addressIdSelected, setAddressIdSelected] = useState<string>("");

    const [appointmentDateTime, setAppointmentDateTime] = useState<Date | null>(null);

    const [requestAddressData, setRequestAddressData] = useState<ProspectAddressData | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setProspectAppointment(null);
            return;
        }
        if (!prospectAppointmentId) return;
        fetchProspectAppointment(prospectAppointmentId);
    }, [prospectAppointmentId, isOpen]);

    useEffect(() => {
        if (!prospectAppointment) return;

        // fill client and address radio options
        const clientTypeSelected = prospectAppointment.prospect.client ? "EXISTING_CLIENT" : "NEW_PROSPECT";
        const addressRadioOptionSelected = prospectAppointment.type === "SHOWROOM"
            ? "SHOWROOM"
            : clientTypeSelected === "NEW_PROSPECT"
                ? "NEW_ADDRESS"
                : "EXISTING_ADDRESS";

        setClientType(clientTypeSelected);
        setAddressRadioSelected(addressRadioOptionSelected);

        if (clientTypeSelected === "EXISTING_CLIENT") {
            setClientIdSelected(prospectAppointment.prospect.client?.id || "");
        }

        // fill appointment and address data
        if (addressRadioOptionSelected !== "SHOWROOM") {
            if (addressRadioOptionSelected === "EXISTING_ADDRESS") {
                setAddressIdSelected(prospectAppointment.appointment.address.id || "");
            } else {
                if (prospectAppointment.appointment.address.postalCode) {
                    fetchNeighborhoods(prospectAppointment.appointment.address.postalCode);
                }
                setRequestAddressData({
                    street: prospectAppointment.appointment.address.street,
                    interiorNumber: prospectAppointment.appointment.address.interiorNumber,
                    exteriorNumber: prospectAppointment.appointment.address.exteriorNumber,
                    postalCode: prospectAppointment.appointment.address.postalCode,
                    city: prospectAppointment.appointment.address.city,
                    municipality: prospectAppointment.appointment.address.municipality,
                    state: prospectAppointment.appointment.address.state,
                    neighborhood: prospectAppointment.appointment.address.neighborhood
                });
            }
        }

    }, [prospectAppointment, isUserAdvisor, userLogged]);

    const handleChangeAddressData = async (e: any, { name, value }: any) => {
        if (name === "postalCode" && value.length === 5) {
            //setLoading(true);
            try {
                const response = await getSettlementsByZipCode(value);
                if (response?.status === 200) {
                    const itemZero = response.data[0];

                    const neighborhoodsList = response.data.map(
                        (neighborhood: any) => {
                            return {
                                key: neighborhood["settlement"],
                                value: neighborhood["settlement"],
                                text: neighborhood["settlement"],
                            };
                        }
                    );
                    setNeighborhoods(neighborhoodsList);

                    setRequestAddressData({
                        ...requestAddressData,
                        postalCode: value ?? "",
                        city: itemZero.city,
                        municipality: itemZero.municipality,
                        state: itemZero.state,
                    });
                } else {
                    throw new Error("El código postal seleccionado no es valido.");
                }
            } catch (error: any) {
                toast.error(error.message);
            } finally {
                //setLoading(false);
            }
        } else {
            setRequestAddressData({
                ...requestAddressData,
                [name]: value ?? "",
            });
        }
    };

    const fetchNeighborhoods = async (zipCode: string) => {
        try {
            if (zipCode.length === 5) {
                const response = await getSettlementsByZipCode(zipCode);
                if (response?.status === 200) {
                    const neighborhoodsList = response.data.map(
                        (neighborhood: any) => {
                            return {
                                key: neighborhood["settlement"],
                                value: neighborhood["settlement"],
                                text: neighborhood["settlement"],
                            };
                        }
                    );
                    setNeighborhoods(neighborhoodsList);
                }
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    }

    const fetchClientAddresses = async (clientId: string) => {
        try {
            const response = await getClientAddressesByClientId(100, 0, "createdAt,asc", clientId);
            const addressList = response?.data.content.map(
                (item: any) => {
                    const addressLine = `${item["street"]} #${item["exteriorNumber"]}, 
                        ${item["interiorNumber"] === "" ? "" : item["interiorNumber"] + ", "}
                        ${item["neighborhood"]}, ${item["postalCode"]}, 
                        ${item["city"]}, ${item["state"]}`;
                    return {
                        key: item["id"],
                        value: item["id"],
                        text: addressLine,
                    };
                }
            );
            setClientAddresses(addressList);
        } catch (error) {
            console.error("Error al obtener la lista de direcciones de el cliente seleccionado:", error);
        }
    };

    const fetchProspectAppointment = async (prospectAppointmentId: string) => {
        try {
            const response = await getProspectsAppointmentById(prospectAppointmentId);

            // fill the form with the data
            if (response?.status === 200) {
                setProspectAppointment(response.data);
            } else {
                throw new Error("Error al obtener el prospect appointment del id seleccionado");
            }


        } catch (error) {
            console.error("Error al obtener la informacion de la cita del prospecto:", error);
        }
    }

    useEffect(() => {
        if (!clientIdSelected) return;
        fetchClientAddresses(clientIdSelected);
    }, [clientIdSelected]);

    const handleSelectedClientAddress = (e: any, { value }: any) => {
        setAddressIdSelected(value);
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {

            if (!addressRadioSelected) {
                throw new Error("Debe seleccionar un tipo de direccion");
            }

            if (!appointmentDateTime) {
                throw new Error("Debe seleccionar una fecha de la cita");
            }

            // creating the request to update the prospect appointment
            const request: ProspectRequest = {
                appointmentData: {
                    addressType: addressRadioSelected,
                    dateTime: appointmentDateTime,
                    selectedAddressId: addressIdSelected,
                    addressData: addressRadioSelected === "NEW_ADDRESS" ? requestAddressData : undefined
                },
            };

            const response = await createFollowUpAppointment(prospectAppointmentId, request);

            if (response?.status === 201) {
                onProspectUpdated();
                toast.success("La cita de seguimiento ha sido creada con exito.");
                onClose();
            } else {
                throw new Error("La cita de seguimiento no ha sido creada exitosamente, favor de contactar al area de TI");
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        onClose();
    };

    return (
        <>
            <Modal
                closeOnDimmerClick
                closeIcon={true}
                size="large"
                onClose={(event) => handleCancelModal(event)}
                open={isOpen}
            >
                <Header icon="calendar plus outline" content="Agendar cita de seguimiento" />
                <ModalContent>
                    <div className="ui form">

                        <FormField required>
                            <label>Fecha y hora de visita</label>
                            <DatePicker
                                showTimeSelect
                                isClearable
                                minDate={new Date()}
                                minTime={setHours(setMinutes(new Date(), 59), 7)}
                                maxTime={setHours(setMinutes(new Date(), 59), 21)}
                                dateFormat="dd/MM/yyyy h:mm aa"
                                selected={appointmentDateTime}
                                placeholderText="Fecha de visita"
                                onChange={(date: Date | null, e) => setAppointmentDateTime(date)}
                                wrapperClassName="date-picker-custom-parent"
                                className="date-picker-custom"
                                name="appointment"
                            />
                        </FormField>

                        {clientType && (
                            <>
                                <FormGroup inline>
                                    <FormRadio
                                        label='Nuevo domicilio'
                                        value='NEW_ADDRESS'
                                        checked={addressRadioSelected === 'NEW_ADDRESS'}
                                        onChange={(event, data) => setAddressRadioSelected(data.value as RadioAddressOptions | undefined)}
                                    />
                                    {clientType === 'EXISTING_CLIENT' && (
                                        <FormRadio
                                            label='Domicilio existente'
                                            value='EXISTING_ADDRESS'
                                            checked={addressRadioSelected === 'EXISTING_ADDRESS'}
                                            onChange={(event, data) => setAddressRadioSelected(data.value as RadioAddressOptions | undefined)}
                                        />
                                    )}
                                    <FormRadio
                                        label='Showroom Luxon'
                                        value='SHOWROOM'
                                        checked={addressRadioSelected === 'SHOWROOM'}
                                        onChange={(event, data) => setAddressRadioSelected(data.value as RadioAddressOptions | undefined)}
                                    />
                                </FormGroup>
                                <Divider hidden />
                            </>
                        )}

                        {addressRadioSelected === 'EXISTING_ADDRESS' && (
                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-advisor">Domicilios registrados del cliente seleccionado</label>}
                                options={clientAddresses}
                                placeholder="Selecciona una opcion"
                                name="selectedClientAddressId"
                                value={addressIdSelected}
                                onChange={handleSelectedClientAddress}
                                required
                                search
                                searchInput={{ id: "form-select-control-advisor" }}
                                clearable
                            />
                        )}

                        {addressRadioSelected === 'NEW_ADDRESS' && (
                            <>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Calle'
                                        placeholder='Nombre de la calle'
                                        name="street"
                                        value={requestAddressData?.street}
                                        onChange={handleChangeAddressData}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Numero exterior'
                                        placeholder='Numero exterior'
                                        name="exteriorNumber"
                                        value={requestAddressData?.exteriorNumber}
                                        onChange={handleChangeAddressData}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Numero interior'
                                        placeholder='Numero interior'
                                        name="interiorNumber"
                                        value={requestAddressData?.interiorNumber}
                                        onChange={handleChangeAddressData}
                                    />
                                </FormGroup>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Codigo postal'
                                        placeholder='Nombre de la calle'
                                        name="postalCode"
                                        value={requestAddressData?.postalCode}
                                        onChange={handleChangeAddressData}
                                        required
                                    />

                                    <FormField
                                        control={Select}
                                        label={<label htmlFor="form-select-control-neighborhood">Colonia</label>}
                                        options={neighborhoods}
                                        placeholder="Selecciona una opción"
                                        name="neighborhood"
                                        value={requestAddressData?.neighborhood}
                                        onChange={handleChangeAddressData}
                                        required
                                        search
                                        searchInput={{ id: "form-select-control-neighborhood" }}
                                        clearable
                                    />
                                </FormGroup>
                                <FormGroup widths='equal'>
                                    <FormField
                                        control={Input}
                                        label='Ciudad'
                                        placeholder='Ciudad'
                                        name="city"
                                        value={requestAddressData?.city}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Municipio'
                                        placeholder='Municipio'
                                        name="municipality"
                                        value={requestAddressData?.municipality}
                                        required
                                    />
                                    <FormField
                                        control={Input}
                                        label='Estado'
                                        placeholder='Estado'
                                        name="state"
                                        value={requestAddressData?.state}
                                        required
                                        readonly
                                    />
                                </FormGroup>
                            </>
                        )}

                    </div>
                </ModalContent>
                <ModalActions>
                    <Button onClick={(event) => handleCancelModal(event)}>
                        <Icon name="cancel" /> Cancelar
                    </Button>
                    <Button color="green" onClick={(e) => handleSubmit(e)} loading={isSubmitting} >
                        <Icon name="checkmark" /> Guardar
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
};

export default FollowUpAppointmentModal;
